import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useJobs } from '../hooks/useJobs';
import './JobsPage.css';
import { parseSalary, formatSalaryDisplay } from '../utils/salary';

const disciplines = ['All', 'Accounting', 'Audit', 'Tax', 'Legal'];

const JOBS_PER_PAGE = 18;

const JobsPage: React.FC = () => {
  const { language } = useLanguage();
  const { jobs: jobsFromDB, loading: jobsLoading, error: jobsError } = useJobs();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>(['All']);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(['All']);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Filters are collapsed behind a toggle on mobile so the vacancy list stays
  // directly under the search bar; on desktop the toggle is hidden via CSS.
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // Calculate the maximum salary from all jobs
  const parsedMaxes = jobsFromDB
    .map(job => {
      const [, maxSal] = parseSalary(job.salary);
      return maxSal === 999999 ? 0 : maxSal; // Ignore unparseable salaries
    })
    .filter(val => val > 0);

  const maxSalaryInJobs = parsedMaxes.length > 0 ? Math.max(...parsedMaxes) : 100000;
  // Round up to nearest 10k for cleaner numbers
  const maxSalary = Math.ceil(maxSalaryInJobs / 10000) * 10000;
  const safeMaxSalary = Math.max(maxSalary, 100000); // Ensure slider has a sensible max

  // Start with a wide salary range to show all jobs by default
  const [salaryRange, setSalaryRange] = useState<[number, number]>([0, safeMaxSalary]);

  // Keep salary slider max in sync with available data so it always spans all jobs
  useEffect(() => {
    setSalaryRange([0, safeMaxSalary]);
  }, [safeMaxSalary]);

  // Bookmark handling (local only)
  const BOOKMARK_KEY = 'mk_bookmarked_job_ids';
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(BOOKMARK_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(BOOKMARK_KEY, JSON.stringify(bookmarkedIds));
    } catch {
      // ignore storage errors
    }
  }, [bookmarkedIds]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('JobsPage - jobsFromDB:', jobsFromDB);
      console.log('JobsPage - jobsLoading:', jobsLoading);
      console.log('JobsPage - jobsError:', jobsError);
    }
  }, [jobsFromDB, jobsLoading, jobsError]);

  // Read search query and discipline from URL on mount
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
    
    const urlDiscipline = searchParams.get('discipline');
    if (urlDiscipline) {
      const parsed = urlDiscipline.split(',').filter((d) => disciplines.includes(d));
      if (parsed.length) {
        setSelectedDisciplines(parsed);
      }
    }
  }, [searchParams]);

  // Reset to page 1 whenever filters/search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDisciplines, selectedLocations, searchQuery, sortBy, salaryRange]);

  // Update URL when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  // Toggle multi-select for disciplines and sync to URL
  const handleDisciplineToggle = (discipline: string) => {
    if (discipline === 'All') {
      setSelectedDisciplines(['All']);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('discipline');
      setSearchParams(newParams);
      return;
    }
    setSelectedDisciplines((prev) => {
      const withoutAll = prev.filter((d) => d !== 'All');
      const exists = withoutAll.includes(discipline);
      const next = exists ? withoutAll.filter((d) => d !== discipline) : [...withoutAll, discipline];
      const finalSelection = next.length === 0 ? ['All'] : next;

      const newParams = new URLSearchParams(searchParams);
      if (finalSelection.includes('All')) {
        newParams.delete('discipline');
      } else {
        newParams.set('discipline', finalSelection.join(','));
      }
      setSearchParams(newParams);

      return finalSelection;
    });
  };

  // Toggle multi-select for locations
  const handleLocationToggle = (location: string) => {
    if (location === 'All') {
      setSelectedLocations(['All']);
      return;
    }
    setSelectedLocations((prev) => {
      const withoutAll = prev.filter((loc) => loc !== 'All');
      const exists = withoutAll.includes(location);
      const next = exists ? withoutAll.filter((loc) => loc !== location) : [...withoutAll, location];
      return next.length === 0 ? ['All'] : next;
    });
  };

  const handleBookmarkToggle = (e: React.MouseEvent, jobId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setBookmarkedIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const isBookmarked = (jobId: string) => bookmarkedIds.includes(jobId);

  // Convert Supabase jobs to the format expected by the component
  // Only use database jobs (no fallback - if database is empty, show empty state)
  const jobsData = jobsFromDB.map((job) => ({
    id: job.id || '',
    titleNl: job.title_nl,
    titleEn: job.title_en,
    discipline: job.discipline,
    location: job.location,
    salary: job.salary,
    descriptionNl: job.description_nl ?? '',
    descriptionEn: job.description_en ?? '',
    postedDate: job.posted_date,
    deadlineDate: job.deadline_date || '',
    uploaderEmail: job.uploader_email || '',
    type: job.type,
    image_url: job.image_url || '',
  }));

  // Derive the location filter options from the actual vacancies. Posting a job
  // in a new city automatically adds it here; distinct spellings show up as
  // separate options, so a typo (the same city appearing twice) is easy to spot.
  const locations = ['All', ...Array.from(
    new Set(jobsData.map((job) => job.location).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'nl'))];

  // Number of active filters, shown as a badge on the mobile filters toggle
  const activeFilterCount =
    selectedDisciplines.filter((d) => d !== 'All').length +
    selectedLocations.filter((loc) => loc !== 'All').length;

  // Filter jobs
  let filteredJobs = jobsData.filter(job => {
    const matchesDiscipline = selectedDisciplines.includes('All') || selectedDisciplines.includes(job.discipline);
    const matchesLocation = selectedLocations.includes('All') || selectedLocations.includes(job.location);
    const title = language === 'nl' ? job.titleNl : job.titleEn;
    const matchesSearch = searchQuery === '' ||
      title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.discipline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.location.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by salary range - only filter if salary can be parsed meaningfully
    const [jobMinSalary, jobMaxSalary] = parseSalary(job.salary);
    const [minRange, maxRange] = salaryRange;
    // If we couldn't parse salary properly (got [0, 999999]), don't filter it out
    // Also, if salary is unparseable, show the job anyway
    const matchesSalary = (jobMinSalary === 0 && jobMaxSalary === 999999) || 
      (jobMaxSalary >= minRange && jobMinSalary <= maxRange);

    const jobMatches = matchesDiscipline && matchesLocation && matchesSearch && matchesSalary;

    // Debug logging in development to help identify filtering issues
    if (process.env.NODE_ENV === 'development' && !jobMatches) {
      console.log('Job filtered out:', {
        jobTitle: title,
        jobDiscipline: job.discipline,
        jobLocation: job.location,
        jobSalary: job.salary,
        parsedSalary: [jobMinSalary, jobMaxSalary],
        selectedDisciplines,
        selectedLocations,
        searchQuery,
        filterRange: [minRange, maxRange],
        matchesDiscipline,
        matchesLocation,
        matchesSearch,
        matchesSalary,
        finalMatch: jobMatches
      });
    }

    return jobMatches;
  });

  // Sort jobs
  filteredJobs = [...filteredJobs].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime();
    } else if (sortBy === 'oldest') {
      return new Date(a.postedDate).getTime() - new Date(b.postedDate).getTime();
    }
    return 0;
  });

  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * JOBS_PER_PAGE,
    currentPage * JOBS_PER_PAGE
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return language === 'nl' ? 'Vandaag' : 'Today';
    if (diffDays === 1) return language === 'nl' ? 'Gisteren' : 'Yesterday';
    if (diffDays < 7) return language === 'nl' ? `${diffDays} dagen geleden` : `${diffDays} days ago`;
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US');
  };

  const resetFilters = () => {
    setSelectedDisciplines(['All']);
    setSelectedLocations(['All']);
    setSearchQuery('');
    // Reset salary range to show all jobs (use dynamic max)
    setSalaryRange([0, safeMaxSalary]);
    setSearchParams({});
    // Scroll to top of jobs section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format number to currency
  const formatCurrency = (num: number): string => {
    return `€${num.toLocaleString('nl-NL')}`;
  };

  return (
    <div className="jobs-page">
      {/* Hero Section */}
      <div className="jobs-hero">
        <div className="jobs-hero-content">
          <h1 className="jobs-hero-title">
            {language === 'nl' ? 'Vind jouw perfecte baan' : 'Find Your Perfect Job'}
          </h1>
          <p className="jobs-hero-subtitle">
            {language === 'nl'
              ? 'Ontdek uitdagende financiële carrièremogelijkheden'
              : 'Discover challenging financial career opportunities'
            }
          </p>
          <div className="jobs-search-bar">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder={language === 'nl' ? 'Zoek op functietitel, discipline...' : 'Search by job title, discipline...'}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="jobs-search-input"
            />
            {searchQuery && (
              <button 
                className="search-clear-btn" 
                onClick={() => handleSearchChange('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="jobs-container">
        <div className="jobs-layout">
          {/* Mobile-only toggle: keeps the vacancy list directly under the
              search bar instead of a full-height filter panel. Hidden on desktop. */}
          <button
            type="button"
            className="filters-toggle"
            onClick={() => setFiltersOpen((prev) => !prev)}
            aria-expanded={filtersOpen}
            aria-controls="jobs-filters"
          >
            <span className="filters-toggle-label">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4"/>
              </svg>
              {language === 'nl' ? 'Filters' : 'Filters'}
              {activeFilterCount > 0 && (
                <span className="filters-toggle-count">{activeFilterCount}</span>
              )}
            </span>
            <svg
              className={`filters-toggle-chevron ${filtersOpen ? 'open' : ''}`}
              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          {/* Sidebar Filters */}
          <aside id="jobs-filters" className={`jobs-sidebar ${filtersOpen ? 'open' : ''}`}>
            <div className="filter-section">
              <h3 className="filter-title">
                {language === 'nl' ? 'Discipline' : 'Discipline'}
              </h3>
              <p className="filter-helper-text">
                {language === 'nl'
                  ? 'Meerdere disciplines mogelijk'
                  : 'Select multiple disciplines'}
              </p>
              <div className="filter-options">
                {disciplines.map(discipline => {
                  const isActive = selectedDisciplines.includes(discipline);
                  return (
                    <label
                      key={discipline}
                      className={`filter-option filter-option-checkbox ${isActive ? 'active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleDisciplineToggle(discipline)}
                        className="filter-checkbox"
                        aria-label={discipline === 'All' ? (language === 'nl' ? 'Alle disciplines' : 'All disciplines') : discipline}
                      />
                      <span className="filter-label-text">
                        {discipline === 'All' ? (language === 'nl' ? 'Alles' : 'All') : discipline}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="filter-section">
              <h3 className="filter-title">
                {language === 'nl' ? 'Locatie' : 'Location'}
              </h3>
              <p className="filter-helper-text">
                {language === 'nl'
                  ? 'Meerdere locaties mogelijk'
                  : 'Select multiple locations'}
              </p>
              <div className="filter-options">
                {locations.map(location => {
                  const isActive = selectedLocations.includes(location);
                  return (
                    <label
                      key={location}
                      className={`filter-option filter-option-checkbox ${isActive ? 'active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleLocationToggle(location)}
                        className="filter-checkbox"
                        aria-label={location === 'All' ? (language === 'nl' ? 'Alle locaties' : 'All locations') : location}
                      />
                      <span className="filter-label-text">
                        {location === 'All' ? (language === 'nl' ? 'Alles' : 'All') : location}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="filter-section">
              <h3 className="filter-title">
                {language === 'nl' ? 'Salaris' : 'Salary'}
              </h3>
              <div className="salary-filter">
                <div className="salary-range-display">
                  {formatCurrency(salaryRange[0])} - {formatCurrency(salaryRange[1])}
                </div>
                <div
                  className="salary-slider-container"
                  style={{
                    '--slider-min': `${((salaryRange[0] / safeMaxSalary) * 100)}%`,
                    '--slider-max': `${((salaryRange[1] / safeMaxSalary) * 100)}%`
                  } as React.CSSProperties & { '--slider-min': string; '--slider-max': string }}
                >
                  <div className="salary-slider-track">
                    <div className="salary-slider-range"></div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={safeMaxSalary}
                    step="10000"
                    value={salaryRange[0]}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setSalaryRange([value, Math.max(value, salaryRange[1])]);
                    }}
                    className="salary-slider salary-slider-min"
                  />
                  <input
                    type="range"
                    min="0"
                    max={safeMaxSalary}
                    step="10000"
                    value={salaryRange[1]}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setSalaryRange([Math.min(value, salaryRange[0]), value]);
                    }}
                    className="salary-slider salary-slider-max"
                  />
                </div>
              </div>
            </div>

            <button className="reset-filters-btn" onClick={resetFilters}>
              {language === 'nl' ? 'Filters wissen' : 'Reset Filters'}
            </button>
          </aside>

          {/* Jobs List */}
          <div className="jobs-main">
            <div className="jobs-header">
              <p className="jobs-count">
                {searchQuery && (
                  <span className="search-term">
                    {language === 'nl' ? `Zoekterm: "${searchQuery}" - ` : `Searching: "${searchQuery}" - `}
                  </span>
                )}
                {language === 'nl'
                  ? `${filteredJobs.length} vacature${filteredJobs.length !== 1 ? 's' : ''} gevonden`
                  : `${filteredJobs.length} job${filteredJobs.length !== 1 ? 's' : ''} found`
                }
                {!jobsLoading && jobsData.length > 0 && filteredJobs.length === 0 && (
                  <span style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    ({jobsData.length} {language === 'nl' ? 'vacature(s) in database' : 'job(s) in database'} - {language === 'nl' ? 'alle gefilterd' : 'all filtered out'})
                  </span>
                )}
              </p>
              <div className="jobs-sort">
                <label htmlFor="sort" className="sort-label">
                  {language === 'nl' ? 'Sorteren:' : 'Sort by:'}
                </label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="newest">{language === 'nl' ? 'Nieuwste' : 'Newest'}</option>
                  <option value="oldest">{language === 'nl' ? 'Oudste' : 'Oldest'}</option>
                </select>
              </div>
            </div>

            <div className="jobs-grid">
              {jobsError ? (
                <div className="no-jobs-message">
                  <svg className="no-jobs-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
                  </svg>
                  <h3>{language === 'nl' ? 'Kan vacatures niet laden' : 'Cannot load jobs'}</h3>
                  <p>{jobsError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem 1.5rem',
                      background: 'var(--mk-orange)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    {language === 'nl' ? 'Opnieuw proberen' : 'Try again'}
                  </button>
                </div>
              ) : jobsLoading ? (
                <div className="jobs-loading">
                  <div className="loading-spinner-large"></div>
                  <p>{language === 'nl' ? 'Vacatures laden...' : 'Loading jobs...'}</p>
                </div>
              ) : filteredJobs.length > 0 ? (
                paginatedJobs.map(job => (
                  <a key={job.id} href={`/jobs/${job.id}`} className="job-card">
                    <div className="job-card-header">
                      <div className="job-discipline-tag-wrapper">
                        <svg className="job-briefcase-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" strokeWidth="2"/>
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" strokeWidth="2"/>
                        </svg>
                        <span className="job-discipline-tag">{job.discipline}</span>
                      </div>
                      <button
                        className={`job-bookmark-btn ${isBookmarked(job.id) ? 'active' : ''}`}
                        onClick={(e) => handleBookmarkToggle(e, job.id)}
                        aria-pressed={isBookmarked(job.id)}
                        aria-label={isBookmarked(job.id)
                          ? (language === 'nl' ? 'Verwijder bookmark' : 'Remove bookmark')
                          : (language === 'nl' ? 'Bewaar vacature' : 'Save job')}
                        title={isBookmarked(job.id)
                          ? (language === 'nl' ? 'Verwijder bookmark' : 'Remove bookmark')
                          : (language === 'nl' ? 'Bewaar vacature' : 'Save job')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>

                    <h2 className="job-title">
                      {language === 'nl' ? job.titleNl : job.titleEn}
                    </h2>

                    <div className="job-meta">
                      <div className="job-meta-item">
                        <svg className="job-location-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <span>{job.location}</span>
                      </div>

                      <div className="job-meta-item">
                        <svg className="job-salary-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-2.21 0-4 .895-4 2s1.79 2 4 2 4 .895 4 2-1.79 2-4 2-4-.895-4-2"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v2m0 8v2m8-6c0 3.314-3.582 6-8 6s-8-2.686-8-6 3.582-6 8-6 8 2.686 8 6z"/>
                        </svg>
                        <span>{formatSalaryDisplay(job.salary, language === 'nl' ? 'nl' : 'en')}</span>
                      </div>
                    </div>

                    <div className="job-card-footer">
                      <div className="job-image-placeholder">
                        {job.image_url ? (
                          <img
                            src={job.image_url}
                            alt={language === 'nl' ? job.titleNl : job.titleEn}
                            loading="lazy"
                          />
                        ) : (
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                            <path d="M21 15l-5-5L5 21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span className="job-date">{formatDate(job.postedDate)}</span>
                    </div>
                  </a>
                ))
              ) : (
                <div className="no-jobs-message">
                  <svg className="no-jobs-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="11" cy="11" r="8" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h3>{language === 'nl' ? 'Geen vacatures gevonden' : 'No jobs found'}</h3>
                  <p>
                    {language === 'nl'
                      ? 'Probeer je filters aan te passen om meer resultaten te zien.'
                      : 'Try adjusting your filters to see more results.'
                    }
                  </p>
                  <button className="reset-filters-btn" onClick={resetFilters}>
                    {language === 'nl' ? 'Alle filters wissen' : 'Clear all filters'}
                  </button>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="jobs-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => { setCurrentPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={currentPage === 1}
                  aria-label={language === 'nl' ? 'Vorige pagina' : 'Previous page'}
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-btn${currentPage === page ? ' active' : ''}`}
                    onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    aria-label={`${language === 'nl' ? 'Pagina' : 'Page'} ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={currentPage === totalPages}
                  aria-label={language === 'nl' ? 'Volgende pagina' : 'Next page'}
                >
                  →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
